import { useId } from "react";
import { CapabilityMap } from "@zylarity/ui/capability-map";
import { capabilityScale } from "@zylarity/contracts";
import "./sample-report-visuals.css";

export type SampleCapability = Readonly<{
  label: string;
  shortLabel: string;
  candidate: number;
  requirement: number;
  evidence: string;
}>;

const competencyScale = capabilityScale;
const level = (value: number) => Math.max(1, Math.min(5, value));

export function CoverageRing({
  value,
  label,
  detail,
}: {
  value: number;
  label: string;
  detail: string;
}) {
  const gradientId = useId();
  const percentage = Math.round(Math.max(0, Math.min(100, value)));
  return (
    <figure className="ir-coverage" aria-label={`${label}: ${percentage}%. ${detail}`}>
      <div className="ir-coverage__graphic" aria-hidden="true">
        <svg viewBox="0 0 220 220">
          <defs>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop className="ir-coverage__gradient-start" offset="0%" />
              <stop className="ir-coverage__gradient-end" offset="100%" />
            </linearGradient>
          </defs>
          <circle className="ir-coverage__halo" cx="110" cy="110" r="101" />
          <circle className="ir-coverage__track" cx="110" cy="110" r="86" />
          <circle
            className="ir-coverage__value"
            cx="110"
            cy="110"
            r="86"
            pathLength="100"
            stroke={`url(#${gradientId})`}
            strokeDasharray={`${percentage} ${100 - percentage}`}
            transform="rotate(-90 110 110)"
          />
          <circle className="ir-coverage__inner" cx="110" cy="110" r="69" />
        </svg>
        <div className="ir-coverage__number">
          <strong>
            {percentage}
            <span>%</span>
          </strong>
          <span>Coverage</span>
        </div>
      </div>
      <figcaption>
        <strong>{label}</strong>
        <span>{detail}</span>
      </figcaption>
    </figure>
  );
}

export function CapabilityRadar({
  capabilities,
  comparison = false,
  job = false,
}: {
  capabilities: readonly SampleCapability[];
  comparison?: boolean;
  job?: boolean;
}) {
  return (
    <div className="ir-shared-capability">
      <CapabilityMap
        axes={capabilities}
        candidate={!job}
        job={job || comparison}
        responsibilityLabels
      />
      <p className="ir-competency-scale">{competencyScale}</p>
    </div>
  );
}

export function CapabilitySignals({
  capabilities,
  mode,
}: {
  capabilities: readonly SampleCapability[];
  mode: "candidate" | "job" | "comparison";
}) {
  return (
    <div className={`ir-signals ir-signals--${mode}`}>
      <ul
        className="ir-signals__list"
        aria-label={
          mode === "job" ? "Role capability requirements" : "Candidate capability signals"
        }
      >
        {capabilities.map((capability) => {
          const candidate = level(capability.candidate);
          const requirement = level(capability.requirement);
          const difference = candidate - requirement;
          const value = mode === "job" ? requirement : candidate;
          const status = difference < 0 ? "gap" : difference === 0 ? "meets" : "exceeds";
          return (
            <li key={capability.label} className="ir-signal">
              <div className="ir-signal__heading">
                <strong>{capability.label}</strong>
                {mode === "comparison" ? (
                  <span className={`ir-signal__status ir-signal__status--${status}`}>
                    {difference < 0
                      ? "Needs follow-up"
                      : difference === 0
                        ? "Same responsibility level"
                        : "Relative strengths"}
                  </span>
                ) : (
                  <span className="ir-signal__value">
                    {value}
                    <span> / 5</span>
                  </span>
                )}
              </div>
              {mode === "comparison" ? (
                <div className="ir-signal__comparison">
                  <div className="ir-signal__bar-row">
                    <span>Candidate</span>
                    <div className="ir-signal__track" aria-hidden="true">
                      <span className="ir-signal__fill" data-level={candidate} />
                    </div>
                    <strong>{candidate}/5</strong>
                  </div>
                  <div className="ir-signal__bar-row ir-signal__bar-row--role">
                    <span>Role</span>
                    <div className="ir-signal__track" aria-hidden="true">
                      <span
                        className="ir-signal__fill ir-signal__fill--role"
                        data-level={requirement}
                      />
                    </div>
                    <strong>{requirement}/5</strong>
                  </div>
                </div>
              ) : (
                <div className="ir-signal__track" aria-hidden="true">
                  <span
                    className={`ir-signal__fill${mode === "job" ? " ir-signal__fill--role" : ""}`}
                    data-level={value}
                  />
                </div>
              )}
              <p className="ir-signal__evidence">
                {mode === "job"
                  ? requirement >= 4
                    ? "Lead delivery, explain design decisions, and coordinate implementation."
                    : "Work independently and apply sound judgment in the plant environment."
                  : capability.evidence}
              </p>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
